import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Camera, ImagesSquare, CheckCircle, Warning, House, Phone, PaperPlaneRight, X, Sparkle } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import TenantTriageChat from "@/pages/TenantTriageChat";
import TenantHistoryView from "@/components/TenantHistoryView";
import { PayRentScreen, PetsScreen, NoticeScreen } from "@/pages/TenantLifecycleScreens";

const BASE = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  "Plumbing", "HVAC / Heat / AC", "Electrical", "Appliance",
  "Pest / Rodent", "Lock / Key / Door", "Leak / Water Damage",
  "Roof / Ceiling", "Other",
];
const URGENCY_OPTIONS = [
  { key: "normal",    label: "Normal",     hint: "Standard request",           bg: "bg-cyan-600" },
  { key: "same_day",  label: "Same Day",   hint: "Needs attention today",      bg: "bg-amber-500" },
  { key: "emergency", label: "Emergency",  hint: "Water, gas, no heat, unsafe",bg: "bg-red-600"   },
];

export default function RequestRadarPortal() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [mode, setMode] = useState("gate"); // gate | triage | form
  const [form, setForm] = useState({
    tenant_submitter_name: "", tenant_submitter_phone: "",
    title: "", description: "", category: "", urgency: "normal",
    unit: "", entry_ok: false,
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    // Read ?asset=X from URL — QR stickers on individual appliances encode this
    const params = new URLSearchParams(window.location.search);
    const assetId = params.get("asset") || "";
    const q = assetId ? `?asset=${encodeURIComponent(assetId)}` : "";
    // Deep-link support: admins share links like /report/{token}#triage or ?mode=triage
    // to drop tenants straight into AI diagnosis (no gate/hunting).
    const hash = (window.location.hash || "").toLowerCase();
    const modeParam = (params.get("mode") || "").toLowerCase();
    if (hash.includes("triage") || modeParam === "triage") setMode("triage");
    else if (hash.includes("form") || modeParam === "form") setMode("form");
    axios.get(`${BASE}/api/public/report/${token}${q}`)
      .then(r => {
        setInfo(r.data);
        // Prefill the Unit field when the token is unit-specific — the
        // backend locks job.unit to the token, but showing it also gives
        // the tenant visual confirmation they're reporting on the right
        // unit (fixes "left the portal, didn't know which unit I picked").
        if (r.data.unit) {
          setForm(f => ({ ...f, unit: r.data.unit }));
        }
        // Pre-fill category + title if the asset context provided them
        if (r.data.asset) {
          setForm(f => ({
            ...f,
            title: r.data.asset.title_prefill || f.title,
            category: r.data.asset.category_prefill || f.category,
          }));
        }
      })
      .catch(e => setErr(e.response?.data?.detail || "Could not load — link may be invalid"));
  }, [token]);

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await axios.post(`${BASE}/api/public/report/${token}/photo`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos(p => [...p, r.data.path]);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Photo upload failed"));
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.tenant_submitter_name.trim()) { toast.error("Please enter your name"); return; }
    if (!form.title.trim() || form.title.trim().length < 3) { toast.error("Please describe the issue in a few words"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${BASE}/api/public/report/${token}/submit`, { ...form, photo_paths: photos });
      setConfirmation(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Submit failed — please try again"));
    } finally { setSubmitting(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white border-2 border-red-500 p-6 text-center">
        <Warning weight="fill" size={40} className="mx-auto text-red-500"/>
        <div className="mt-3 font-display font-black text-2xl uppercase">Link Invalid</div>
        <div className="mt-2 text-sm text-slate-700">{err}</div>
      </div>
    </div>
  );
  if (!info) return <div className="min-h-screen bg-slate-100 flex items-center justify-center text-slate-500 font-black uppercase tracking-widest">Loading…</div>;

  if (confirmation) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4" data-testid="request-radar-confirmed">
      <div className="max-w-md w-full bg-white border-2 border-[#0f172a] p-8 text-center">
        <div className="h-16 w-16 mx-auto bg-emerald-600 text-white rounded-full flex items-center justify-center">
          <CheckCircle weight="fill" size={40}/>
        </div>
        <h1 className="mt-4 font-display font-black text-3xl uppercase">Got It!</h1>
        <p className="mt-2 text-sm text-slate-700">
          Your request has been sent to <b>{info.crew_name}</b>. They'll reach out shortly.
        </p>
        <div className="mt-4 p-3 border-2 border-[#0f172a] bg-slate-50">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Confirmation Ref</div>
          <div className="mt-1 font-display font-black text-2xl tracking-wider">{confirmation.confirmation_ref}</div>
        </div>
        {info.crew_phone && (
          <div className="mt-4 text-xs text-slate-600 inline-flex items-center gap-1">
            <Phone size={12}/> Need to add something? Call {info.crew_phone}
          </div>
        )}
      </div>
    </div>
  );

  // ── Front gate — pick AI helper or classic form ─────────────────────
  if (mode === "gate") {
    const hasManagement = (info?.modules || ["maintenance"]).includes("management");
    const stub = (label) => () => toast.info(`${label} — coming soon in your Management portal.`);
    return (
      <div className="min-h-screen bg-slate-100" data-testid="request-radar-gate">
        <header className="bg-[#0f172a] text-white" style={info.brand_color ? { background: info.brand_color } : {}}>
          <div className="max-w-lg mx-auto px-3 py-4 flex items-center gap-3">
            <div className="h-9 w-9 bg-white flex items-center justify-center"><Logo size={22}/></div>
            <div className="flex-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">{info.crew_name} · Tenant Portal</div>
              <div className="font-display font-black text-lg leading-tight">How can we help today?</div>
            </div>
          </div>
          <div className="max-w-lg mx-auto px-3 pb-3 text-xs inline-flex items-center gap-1">
            <House size={12}/> {info.property_address}{info.unit ? ` · Unit ${info.unit}` : ""}
          </div>
        </header>

        <div className="max-w-lg mx-auto p-3 space-y-3">
          {info.emergency_map_url && (
            <>
              <div className="text-[10px] font-black uppercase tracking-widest text-red-500 pt-1">🛡️ Safety First</div>
              <a href={info.emergency_map_url} target="_blank" rel="noopener noreferrer"
                 data-testid="tenant-emergency-map-tile"
                 className="block w-full text-left p-4 bg-red-600 text-white border-2 border-[#0f172a] hover:shadow-[6px_6px_0px_#0f172a] transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 text-2xl">🛡️</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-black uppercase tracking-widest opacity-90">If water is leaking · gas smells</div>
                    <div className="font-display font-black text-xl uppercase tracking-tight leading-tight">Emergency Map</div>
                  </div>
                  <div className="text-2xl shrink-0 opacity-70">›</div>
                </div>
                <div className="mt-2 text-sm opacity-95 leading-relaxed">
                  Find every shut-off valve, GFI outlet, electrical panel and HVAC filter — for your unit.
                </div>
              </a>
            </>
          )}
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 pt-1">🛠️ Maintenance</div>
          <button onClick={() => setMode("triage")} data-testid="rr-choose-triage"
            className="w-full text-left p-5 bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white border-2 border-[#0f172a] hover:shadow-[6px_6px_0px_#0f172a] transition-shadow">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0"><Sparkle weight="fill" size={24}/></div>
              <div className="flex-1">
                <div className="text-[11px] font-black uppercase tracking-widest opacity-90">Recommended · 60 seconds</div>
                <div className="font-display font-black text-xl uppercase tracking-tight">Try the AI Helper</div>
              </div>
            </div>
            <div className="mt-2 text-sm opacity-95 leading-relaxed">
              Walks you through common fixes. If it needs a tech, we gather everything for them first.
            </div>
          </button>
          <button onClick={() => setMode("form")} data-testid="rr-choose-form"
            className="w-full text-left p-4 bg-white border-2 border-[#0f172a] hover:bg-slate-50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-slate-100 flex items-center justify-center border-2 border-[#0f172a]"><PaperPlaneRight weight="fill" size={18}/></div>
              <div className="flex-1">
                <div className="font-display font-black text-lg uppercase tracking-tight">Submit a Request</div>
                <div className="text-xs text-slate-600 mt-0.5">Classic form — best when you've already tried a fix.</div>
              </div>
            </div>
          </button>

          {info.portal_enabled && (
            <button onClick={() => setMode("history")} data-testid="rr-choose-history"
              className="w-full text-left p-4 bg-cyan-500 hover:bg-cyan-600 text-white border-2 border-[#0f172a] transition-colors">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-white/20 flex items-center justify-center border-2 border-white/30">📋</div>
                <div className="flex-1">
                  <div className="font-display font-black text-lg uppercase tracking-tight">My Requests</div>
                  <div className="text-xs text-white/90 mt-0.5">See status, ETA, and messages for your prior requests.</div>
                </div>
              </div>
            </button>
          )}

          {hasManagement && (
            <>
              <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-600 pt-3">🏘️ Living Here</div>
              <div className="grid grid-cols-2 gap-2" data-testid="tenant-management-tiles">
                <button onClick={() => setMode("pay-rent")} data-testid="tenant-pay-rent"
                  className="p-4 bg-white border-2 border-fuchsia-600 hover:bg-fuchsia-50 text-left">
                  <div className="text-2xl">💵</div>
                  <div className="font-black text-sm mt-1">Pay Rent</div>
                  <div className="text-[10px] text-slate-500">Secure Stripe checkout</div>
                </button>
                <button onClick={stub("Late Fee Question")} data-testid="tenant-late-fee"
                  className="p-4 bg-white border-2 border-fuchsia-600 hover:bg-fuchsia-50 text-left">
                  <div className="text-2xl">📄</div>
                  <div className="font-black text-sm mt-1">Late Fees</div>
                  <div className="text-[10px] text-slate-500">Payment plan · balance</div>
                </button>
                <button onClick={() => setMode("pets")} data-testid="tenant-pets"
                  className="p-4 bg-white border-2 border-fuchsia-600 hover:bg-fuchsia-50 text-left">
                  <div className="text-2xl">🐾</div>
                  <div className="font-black text-sm mt-1">Update Pets</div>
                  <div className="text-[10px] text-slate-500">Add or remove pet on file</div>
                </button>
                <button onClick={() => setMode("notice")} data-testid="tenant-notice"
                  className="p-4 bg-white border-2 border-fuchsia-600 hover:bg-fuchsia-50 text-left">
                  <div className="text-2xl">📅</div>
                  <div className="font-black text-sm mt-1">Notice to Vacate</div>
                  <div className="text-[10px] text-slate-500">30 · 60 · 90 day notice</div>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (mode === "pay-rent") return <PayRentScreen token={token} info={info} onBack={() => setMode("gate")}/>;
  if (mode === "pets") return <PetsScreen token={token} info={info} onBack={() => setMode("gate")}/>;
  if (mode === "notice") return <NoticeScreen token={token} info={info} onBack={() => setMode("gate")}/>;

  // ── Tenant Portal history mode (Roadmap #7) ─────────────────────────
  if (mode === "history") {
    return <TenantHistoryView token={token} info={info} onBack={() => setMode("gate")}/>;
  }

  // ── AI Triage mode ──────────────────────────────────────────────────
  if (mode === "triage") {
    return (
      <TenantTriageChat
        token={token}
        info={info}
        onBack={() => setMode("gate")}
        onEscalated={() => { /* confirmation shown inside triage chat */ }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="request-radar-portal">
      <header className="bg-[#0f172a] text-white" style={info.brand_color ? { background: info.brand_color } : {}}>
        <div className="max-w-lg mx-auto px-3 py-4 flex items-center gap-3">
          <div className="h-9 w-9 bg-white flex items-center justify-center"><Logo size={22}/></div>
          <div className="flex-1">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">{info.crew_name} · RequestRadar™</div>
            <div className="font-display font-black text-lg leading-tight">Report an Issue</div>
          </div>
        </div>
        <div className="max-w-lg mx-auto px-3 pb-3 text-xs inline-flex items-center gap-1">
          <House size={12}/> {info.property_address}{info.unit ? ` · Unit ${info.unit}` : ""}
        </div>
      </header>

      <div className="max-w-lg mx-auto p-3 space-y-3 pb-32">
        {info.emergency_map_url && (
          <a href={info.emergency_map_url} target="_blank" rel="noopener noreferrer"
             data-testid="tenant-emergency-map-tile"
             className="block p-4 bg-red-600 text-white border-4 border-white shadow-[4px_4px_0_#0f172a] hover:brightness-110 transition">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 shrink-0 rounded-full bg-white flex items-center justify-center text-2xl">
                🛡️
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Safety First</div>
                <div className="font-display font-black text-lg leading-tight">Emergency Map</div>
                <div className="text-xs opacity-90 leading-snug mt-0.5">Find water &amp; gas shut-offs, GFI outlets, electrical panel &amp; more for your unit.</div>
              </div>
              <div className="text-2xl shrink-0">›</div>
            </div>
          </a>
        )}

        <div className="p-3 bg-white border-2 border-[#0f172a]">
          <FormField label="Your Name">
            <input value={form.tenant_submitter_name}
              onChange={e => setForm(f => ({ ...f, tenant_submitter_name: e.target.value }))}
              data-testid="rr-name"
              className="w-full h-11 px-3 border-2 border-[#0f172a] font-medium text-sm"/>
          </FormField>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <FormField label="Phone (optional)">
              <input value={form.tenant_submitter_phone} type="tel"
                onChange={e => setForm(f => ({ ...f, tenant_submitter_phone: e.target.value }))}
                data-testid="rr-phone"
                className="w-full h-11 px-3 border-2 border-[#0f172a] font-medium text-sm"/>
            </FormField>
            <FormField label={info.unit ? "Unit (locked to your link)" : "Unit (if any)"}>
              <input value={form.unit}
                onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                disabled={!!info.unit}
                data-testid="rr-unit"
                className={`w-full h-11 px-3 border-2 border-[#0f172a] font-medium text-sm ${info.unit ? "bg-slate-100 text-slate-600 cursor-not-allowed" : ""}`}/>
            </FormField>
          </div>
        </div>

        <div className="p-3 bg-white border-2 border-[#0f172a]">
          <FormField label="Short Title (e.g. Kitchen sink leaking)">
            <input value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              data-testid="rr-title" maxLength={140}
              className="w-full h-11 px-3 border-2 border-[#0f172a] font-medium text-sm"/>
          </FormField>
          <div className="mt-3">
            <FormField label="Category">
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                data-testid="rr-category"
                className="w-full h-11 px-3 border-2 border-[#0f172a] font-bold text-sm">
                <option value="">— Choose —</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </FormField>
          </div>
          <div className="mt-3">
            <FormField label="Describe the issue">
              <textarea value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                data-testid="rr-description" rows={4} maxLength={4000}
                placeholder="Tell us what's happening. When did it start? What have you tried?"
                className="w-full px-3 py-2 border-2 border-[#0f172a] font-medium text-sm"/>
            </FormField>
          </div>
        </div>

        <div className="p-3 bg-white border-2 border-[#0f172a]">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Urgency</div>
          <div className="grid grid-cols-3 gap-1.5" data-testid="rr-urgency-group">
            {URGENCY_OPTIONS.map(u => (
              <button key={u.key} onClick={() => setForm(f => ({ ...f, urgency: u.key }))}
                data-testid={`rr-urgency-${u.key}`}
                className={`h-16 flex flex-col items-center justify-center px-1 text-white font-black uppercase text-xs tracking-widest ${
                  form.urgency === u.key ? u.bg : "bg-slate-300 text-slate-500 hover:bg-slate-400"
                }`}>
                <span>{u.label}</span>
                <span className="text-[8px] font-bold normal-case tracking-normal mt-0.5 opacity-90">{u.hint}</span>
              </button>
            ))}
          </div>
          <label className="mt-3 flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.entry_ok}
              onChange={e => setForm(f => ({ ...f, entry_ok: e.target.checked }))}
              data-testid="rr-entry-ok" className="h-4 w-4"/>
            <span className="text-xs font-bold">OK to enter with a key or code if I'm not home</span>
          </label>
        </div>

        <div className="p-3 bg-white border-2 border-[#0f172a]">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Photos (optional but helpful)</div>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="sr-only"
            onChange={e => { if (e.target.files?.length) { uploadFiles(Array.from(e.target.files)); e.target.value = ""; }}}/>
          <input ref={galleryRef} type="file" accept="image/*" multiple className="sr-only"
            onChange={e => { if (e.target.files?.length) { uploadFiles(Array.from(e.target.files)); e.target.value = ""; }}}/>
          <div className="flex gap-2">
            <button onClick={() => cameraRef.current?.click()} disabled={uploading}
              data-testid="rr-camera"
              className="flex-1 h-11 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1">
              <Camera weight="fill" size={14}/> {uploading ? "Uploading…" : "Take Photo"}
            </button>
            <button onClick={() => galleryRef.current?.click()} disabled={uploading}
              data-testid="rr-gallery"
              className="flex-1 h-11 border-2 border-[#0f172a] font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1">
              <ImagesSquare weight="fill" size={14}/> Gallery
            </button>
          </div>
          {photos.length > 0 && (
            <div className="mt-2 flex gap-2 overflow-x-auto pb-1" data-testid="rr-photo-strip">
              {photos.map((p, i) => (
                <div key={i} className="relative flex-shrink-0">
                  <div className="h-20 w-20 bg-slate-200 border-2 border-[#0f172a] flex items-center justify-center text-[10px] font-black text-slate-600">
                    ✓ Photo {i + 1}
                  </div>
                  <button onClick={() => setPhotos(ps => ps.filter((_, idx) => idx !== i))}
                    data-testid={`rr-rm-photo-${i}`}
                    className="absolute -top-1.5 -right-1.5 h-5 w-5 bg-red-600 text-white flex items-center justify-center rounded-full">
                    <X size={10}/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 bg-white border-t-2 border-[#0f172a] p-3">
        <div className="max-w-lg mx-auto">
          <button onClick={submit} disabled={submitting || uploading}
            data-testid="rr-submit"
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black uppercase text-sm tracking-widest inline-flex items-center justify-center gap-2">
            <PaperPlaneRight weight="fill" size={16}/> {submitting ? "Sending…" : "Send Request"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
